
import React, { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { Deal } from "@/api/entities";
import { User } from "@/api/entities";
import { appClient } from "@/api/appClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  Calendar,
  Search,
  Filter,
  Receipt,
  Plus
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format, startOfDay, differenceInCalendarDays } from "date-fns";
import { parseDate } from "@/lib/localizedDateUtils";
import { isDatePast, getDaysOverdue, parseDateSafe } from "@/lib/timezoneUtils";
import { createPageUrl } from "@/utils";

export default function Finance() {
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]); // This will now hold Supabase contacts
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("by_deal"); // Changed default to by_deal
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // New state for payment recording
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: "",
    payment_method: "cash",
    reference_number: "",
    installment_number: "",
    notes: ""
  });

  useEffect(() => {
    loadData();
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await User.me();
      setUser(userData);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const calculateDealPaymentStatus = (deal) => {
    if (!deal.payment_plan || deal.payment_plan.length === 0) return "no_plan";
    
    const totalDue = deal.total_value || 0;
    const totalPaid = deal.total_paid || 0;
    
    if (totalPaid >= totalDue) return "paid_in_full";
    if (totalPaid === 0) return "not_started";
    
    // Check for overdue installments
    const today = startOfDay(new Date());
    const hasOverdue = deal.payment_plan.some(inst => {
      if (inst.status === "paid" || inst.status === "partial") return false;
      const dueDate = parseDate(inst.due_date);
      return dueDate && dueDate < today;
    });
    
    if (hasOverdue) return "overdue";
    return "in_progress";
  };

  useEffect(() => {
    let filtered = [...deals];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(deal => {
        const contact = contacts.find(c => c.contact_id === deal.contact_id); // Changed to c.contact_id
        const contactName = contact 
          ? `${contact.first_name} ${contact.last_name}`.toLowerCase()
          : "";
        return (
          ((deal.name || deal.deal_name || '').toLowerCase().includes(query)) ||
          contactName.includes(query)
        );
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(deal => {
        const status = calculateDealPaymentStatus(deal);
        return status === statusFilter;
      });
    }

    setFilteredDeals(filtered);
  }, [deals, contacts, searchQuery, statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      console.log("🔵 Loading deals and contacts...");
      
      // Load deals from the unified app API client
      const dealsData = await Deal.list("-created_date");
      
      // Load contacts from Supabase
      const contactsResponse = await appClient.functions.invoke('getSupabaseContacts', {});
      const contactsData = contactsResponse.data.success ? contactsResponse.data.contacts : [];
      
      console.log("✅ Loaded deals:", dealsData.length);
      console.log("✅ Loaded contacts:", contactsData.length);
      
      // Only include deals that are closed won (have payment plans)
      const closedDeals = dealsData.filter(d => d.stage === "closed_won");
      setDeals(closedDeals);
      setContacts(contactsData);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  };

  const handleRecordPayment = (deal) => {
    setSelectedDeal(deal);
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: "",
      payment_method: "cash",
      reference_number: "",
      installment_number: "",
      notes: ""
    });
    setShowPaymentDialog(true);
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      alert("Please enter a valid payment amount");
      return;
    }
    if (!selectedDeal) return;

    setIsProcessing(true);
    try {
      const paymentAmount = Number(paymentForm.amount);
      
      // Create payment record
      const newPayment = {
        payment_date: paymentForm.payment_date,
        amount: paymentAmount,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        installment_number: paymentForm.installment_number ? Number(paymentForm.installment_number) : null,
        notes: paymentForm.notes || null,
        recorded_by: user?.email || "Unknown",
        recorded_date: new Date().toISOString()
      };

      // Update payments_received array
      const updatedPaymentsReceived = [...(selectedDeal.payments_received || []), newPayment];
      
      // Update total_paid
      const updatedTotalPaid = (selectedDeal.total_paid || 0) + paymentAmount;
      
      // Update balance_due
      const updatedBalanceDue = (selectedDeal.total_value || 0) - updatedTotalPaid;

      // Update installment status if applicable
      let updatedPaymentPlan = selectedDeal.payment_plan || [];
      if (paymentForm.installment_number) {
        updatedPaymentPlan = updatedPaymentPlan.map(inst => {
          if (inst.installment_number === Number(paymentForm.installment_number)) {
            const newAmountPaid = (inst.amount_paid || 0) + paymentAmount;
            const instAmount = inst.amount || 0;
            
            return {
              ...inst,
              amount_paid: newAmountPaid,
              status: newAmountPaid >= instAmount ? "paid" : "partial"
            };
          }
          return inst;
        });
      }

      // Determine overall payment status
      let paymentStatus = "in_progress";
      if (updatedTotalPaid >= (selectedDeal.total_value || 0)) {
        paymentStatus = "paid_in_full";
      } else if (updatedTotalPaid === 0) {
        paymentStatus = "not_started";
      } else {
        const todayCheck = startOfDay(new Date());
        const hasOverdue = updatedPaymentPlan.some(inst => {
          if (inst.status === "paid" || inst.status === "partial") return false;
          const dueDate = parseDate(inst.due_date);
          return dueDate && dueDate < todayCheck;
        });
        if (hasOverdue) {
          paymentStatus = "overdue";
        }
      }

      // Update the deal
      await Deal.update(selectedDeal.id, {
        payments_received: updatedPaymentsReceived,
        total_paid: updatedTotalPaid,
        balance_due: updatedBalanceDue,
        payment_plan: updatedPaymentPlan,
        payment_status: paymentStatus // Add payment_status to deal for easier filtering/display
      });

      setShowPaymentDialog(false);
      await loadData(); // Reload all data to reflect changes
    } catch (error) {
      console.error("Error recording payment:", error);
      alert("Error recording payment: " + error.message);
    }
    setIsProcessing(false);
  };

  const calculateSummary = () => {
    const summary = {
      totalRevenue: 0,
      totalPaid: 0,
      outstanding: 0,
      overdue: 0,
      dealsCount: deals.length
    };

    deals.forEach(deal => {
      summary.totalRevenue += deal.total_value || 0;
      summary.totalPaid += deal.total_paid || 0;
      summary.outstanding += (deal.total_value || 0) - (deal.total_paid || 0);
      
      // Calculate overdue amount
      const todayCalc = startOfDay(new Date());
      if (deal.payment_plan) {
        deal.payment_plan.forEach(inst => {
          const dueDate = parseDate(inst.due_date);
          if (inst.status !== "paid" && dueDate && dueDate < todayCalc) {
            summary.overdue += (inst.amount || 0) - (inst.amount_paid || 0);
          }
        });
      }
    });

    return summary;
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.contact_id === contactId); // Changed to c.contact_id
    if (!contact) return "Unknown";
    return `${contact.first_name} ${contact.last_name}`;
  };

  const navigateToDeal = (dealId) => {
    window.location.href = createPageUrl("DealDetail") + "?id=" + dealId;
  };

  const getStatusBadge = (status) => {
    const configs = {
      paid_in_full: { label: "Paid in Full", className: "bg-emerald-100 text-emerald-700" },
      in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700" },
      overdue: { label: "Overdue", className: "bg-rose-100 text-rose-700" },
      not_started: { label: "Not Started", className: "bg-slate-100 text-slate-700" },
      no_plan: { label: "No Payment Plan", className: "bg-amber-100 text-amber-700" }
    };
    const config = configs[status] || configs.no_plan;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatType = (type) => {
    if (!type || typeof type !== 'string') return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const summary = calculateSummary();

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Finance</h1>
          <p className="text-slate-600">Track payments, revenue, and outstanding balances</p>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-600">Total Revenue</p>
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-slate-900">
                ${summary.totalRevenue.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">{summary.dealsCount} deals</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-600">Amount Paid</p>
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-3xl font-bold text-emerald-700">
                ${summary.totalPaid.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {summary.totalRevenue > 0 
                  ? ((summary.totalPaid / summary.totalRevenue) * 100).toFixed(1) 
                  : 0}% collected
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-600">Outstanding</p>
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-700">
                ${summary.outstanding.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1">To be collected</p>
            </CardContent>
          </Card>

          <Card className="border-rose-200 bg-rose-50/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-rose-700">Overdue</p>
                <AlertCircle className="w-5 h-5 text-rose-600" />
              </div>
              <p className="text-3xl font-bold text-rose-700">
                ${summary.overdue.toLocaleString()}
              </p>
              <p className="text-xs text-rose-600 mt-1">Requires attention</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by deal or contact name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px] border-slate-300">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="paid_in_full">Paid in Full</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
            {/* Removed "Summary" button as cards are always visible. Using "All Deals" instead for deals list view */}
            <Button
              variant={viewMode === "by_deal" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("by_deal")}
              className={viewMode === "by_deal" ? "bg-white shadow-sm" : ""}
            >
              All Deals
            </Button>
            <Button
              variant={viewMode === "by_contact" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("by_contact")}
              className={viewMode === "by_contact" ? "bg-white shadow-sm" : ""}
            >
              By Contact
            </Button>
            <Button
              variant={viewMode === "timeline" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("timeline")}
              className={viewMode === "timeline" ? "bg-white shadow-sm" : ""}
            >
              Timeline
            </Button>
          </div>
        </div>

        {/* Deals List (now under 'by_deal' view mode) */}
        {viewMode === "by_deal" && (
          <div className="space-y-4">
            {filteredDeals.map(deal => {
              const status = calculateDealPaymentStatus(deal);
              const totalDue = deal.total_value || 0;
              const totalPaid = deal.total_paid || 0;
              const remaining = totalDue - totalPaid;
              const nextPayment = (deal.payment_plan || [])
                .filter(p => p.status !== "paid")
                .sort((a, b) => (parseDate(a.due_date)?.getTime() || 0) - (parseDate(b.due_date)?.getTime() || 0))[0];

              return (
                <Card 
                  key={deal.id} 
                  className="border-slate-200"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <button
                                onClick={() => navigateToDeal(deal.id)}
                                className="text-lg font-bold text-blue-600 hover:text-blue-700 hover:underline text-left"
                            >
                                {deal.name || deal.deal_name}
                            </button>
                            {getStatusBadge(status)}
                        </div>
                        <p className="text-sm text-slate-600">Contact: {getContactName(deal.contact_id)}</p>
                        {deal.contract_signed_date && (
                            <p className="text-xs text-slate-500 mt-1">
                                Contract signed: {safeFormat(new Date(deal.contract_signed_date), "MMM d, yyyy")}
                            </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleRecordPayment(deal)}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Record Payment
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Total Value</p>
                        <p className="font-semibold text-slate-900">${totalDue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Paid</p>
                        <p className="font-semibold text-emerald-700">${totalPaid.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Remaining</p>
                        <p className="font-semibold text-blue-700">${remaining.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Progress</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-600 transition-all"
                              style={{ width: `${totalDue > 0 ? (totalPaid / totalDue) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-600">
                            {totalDue > 0 ? ((totalPaid / totalDue) * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {nextPayment && status !== "paid_in_full" && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Next Payment Due</p>
                            <p className="text-xs text-amber-700">{nextPayment.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-amber-900">
                              ${((nextPayment.amount || 0) - (nextPayment.amount_paid || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs text-amber-700">
                              Due: {safeFormat(new Date(nextPayment.due_date), "MMM d, yyyy")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {deal.payment_plan && deal.payment_plan.length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-4">
                        {deal.payment_plan.slice(0, 3).map((inst, index) => {
                          const isDue = isDatePast(inst.due_date);
                          const isPaid = inst.status === "paid";
                          const parsedDueDate = parseDateSafe(inst.due_date);
                          
                          return (
                            <div 
                              key={inst.installment_number || index} // Use installment_number as key, fallback to index
                              className={`text-xs px-2 py-1 rounded ${
                                isPaid 
                                  ? "bg-emerald-100 text-emerald-700"
                                  : isDue 
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              ${inst.amount.toLocaleString()} - {parsedDueDate ? safeFormat(parsedDueDate, "MMM d") : ''}
                              {isPaid && " ✓"}
                              {!isPaid && isDue && ` (${getDaysOverdue(inst.due_date)}d overdue)`}
                            </div>
                          );
                        })}
                        {deal.payment_plan.length > 3 && (
                          <div className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">
                            +{deal.payment_plan.length - 3} more
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {filteredDeals.length === 0 && (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center text-slate-400">
                  <Receipt className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Nenhum negócio encontrado com os filtros aplicados</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {viewMode === "by_contact" && (
          <div className="space-y-6">
            {Object.entries(
              filteredDeals.reduce((acc, deal) => {
                const contactId = deal.contact_id;
                if (!acc[contactId]) acc[contactId] = [];
                acc[contactId].push(deal);
                return acc;
              }, {})
            ).map(([contactId, contactDeals]) => {
              const totalValue = contactDeals.reduce((sum, d) => sum + (d.total_value || 0), 0);
              const totalPaid = contactDeals.reduce((sum, d) => sum + (d.total_paid || 0), 0);

              return (
                <Card key={contactId} className="border-slate-200">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{getContactName(contactId)}</CardTitle>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">
                          Total: <span className="font-bold text-slate-900">${totalValue.toLocaleString()}</span>
                        </p>
                        <p className="text-sm text-emerald-600">
                          Paid: <span className="font-bold">${totalPaid.toLocaleString()}</span>
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {contactDeals.map(deal => {
                        const status = calculateDealPaymentStatus(deal);
                        return (
                          <div
                            key={deal.id}
                            onClick={() => navigateToDeal(deal.id)}
                            className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-slate-900">{deal.name || deal.deal_name}</p>
                                <p className="text-sm text-slate-600">
                                  ${(deal.total_paid || 0).toLocaleString()} / ${(deal.total_value || 0).toLocaleString()}
                                </p>
                              </div>
                              {getStatusBadge(status)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {viewMode === "timeline" && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Payment Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {filteredDeals
                  .flatMap(deal => 
                    (deal.payment_plan || []).map(inst => ({
                      ...inst,
                      dealName: deal.name || deal.deal_name,
                      dealId: deal.id,
                      contactName: getContactName(deal.contact_id)
                    }))
                  )
                  .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                  .map((inst, index) => {
                    const isDue = isPast(new Date(inst.due_date));
                    const isPaid = inst.status === "paid";

                    return (
                      <div
                        key={inst.installment_number ? `${inst.dealId}-${inst.installment_number}` : `${inst.dealId}-${index}`} // More robust key
                        onClick={() => navigateToDeal(inst.dealId)}
                        className={`p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                          isPaid
                            ? "border-emerald-600 bg-emerald-50"
                            : isDue
                            ? "border-rose-600 bg-rose-50"
                            : "border-blue-600 bg-blue-50"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{inst.dealName}</p>
                            <p className="text-sm text-slate-600">{inst.contactName}</p>
                            <p className="text-sm text-slate-600 mt-1">{inst.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-slate-900">
                              ${inst.amount.toLocaleString()}
                            </p>
                            <p className="text-sm text-slate-600">
                              {safeFormat(new Date(inst.due_date), "MMM d, yyyy")}
                            </p>
                            {isPaid && (
                              <Badge className="bg-emerald-100 text-emerald-700 mt-2">Paid</Badge>
                            )}
                            {!isPaid && isDue && (
                              <Badge className="bg-rose-100 text-rose-700 mt-2">
                                {differenceInDays(new Date(), new Date(inst.due_date))}d overdue
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Recording Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>
                Record a payment for {selectedDeal?.name || selectedDeal?.deal_name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="payment_date">Payment Date <span className="text-red-500">*</span></Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  className="border-slate-300"
                />
              </div>

              <div>
                <Label htmlFor="amount">Amount <span className="text-red-500">*</span></Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  className="border-slate-300"
                />
                {selectedDeal && (
                  <p className="text-xs text-slate-500 mt-1">
                    Balance due: ${(selectedDeal.total_value - (selectedDeal.total_paid || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="payment_method">Payment Method <span className="text-red-500">*</span></Label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(value) => setPaymentForm({...paymentForm, payment_method: value})}
                >
                  <SelectTrigger className="border-slate-300" id="payment_method">
                    <SelectValue placeholder="Select a method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{formatType("cash")}</SelectItem>
                    <SelectItem value="check">{formatType("check")}</SelectItem>
                    <SelectItem value="credit_card">{formatType("credit_card")}</SelectItem>
                    <SelectItem value="debit_card">{formatType("debit_card")}</SelectItem>
                    <SelectItem value="wire_transfer">{formatType("wire_transfer")}</SelectItem>
                    <SelectItem value="zelle">{formatType("zelle")}</SelectItem>
                    <SelectItem value="venmo">{formatType("venmo")}</SelectItem>
                    <SelectItem value="paypal">{formatType("paypal")}</SelectItem>
                    <SelectItem value="other">{formatType("other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedDeal?.payment_plan && selectedDeal.payment_plan.length > 0 && (
                <div>
                  <Label htmlFor="installment_number">Apply to Installment (Optional)</Label>
                  <Select
                    value={paymentForm.installment_number || ""} // Ensure value is "" for no selection in Select component
                    onValueChange={(value) => setPaymentForm({...paymentForm, installment_number: value === "" ? null : value})}
                  >
                    <SelectTrigger className="border-slate-300" id="installment_number">
                      <SelectValue placeholder="General payment (not for specific installment)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={""}>General payment</SelectItem> {/* Changed null to "" for select */}
                      {selectedDeal.payment_plan.map((inst) => (
                        <SelectItem 
                            key={inst.installment_number} 
                            value={inst.installment_number.toString()}
                            disabled={inst.status === "paid"} // Disable if already paid in full
                        >
                          #{inst.installment_number}: {inst.description} - ${(inst.amount || 0).toLocaleString()} 
                          {inst.status !== "paid" && inst.status !== "partial" && ` (Due)`}
                          {inst.status === "partial" && ` (Partial)`}
                          {inst.status === "paid" && ` (Paid)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="reference_number">Reference Number (Optional)</Label>
                <Input
                  id="reference_number"
                  placeholder="Check #, Transaction ID, etc."
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                  className="border-slate-300"
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this payment..."
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  className="border-slate-300"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPaymentDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitPayment}
                disabled={isProcessing || !paymentForm.amount || Number(paymentForm.amount) <= 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? "Recording..." : "Record Payment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
